/**
 * Loads pre-signed Learnosity activity data if available.
 *
 * During CI builds for the static S3 demo, the presign-learnosity.mjs script
 * generates presigned-learnosity.json with HMAC-signed Learnosity requests.
 * This file will NOT exist in local development — the import resolves to an
 * empty record so local dev falls through to the on-demand Vite signing endpoint.
 *
 * Uses Vite's import.meta.glob (eager) which returns {} when no file matches,
 * avoiding runtime errors for missing files.
 */

type PresignedMap = Record<string, unknown>;

const modules = import.meta.glob<PresignedMap>("./presigned-learnosity.json", {
  eager: true,
  import: "default"
});

const presignedData: PresignedMap =
  modules["./presigned-learnosity.json"] ?? {};

/**
 * Look up a pre-signed Learnosity activity request by activity UUID + item list.
 * Returns the full signed response or undefined if not pre-signed.
 */
export const getPresignedActivity = (
  activityUuid: string,
  itemUuids: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any | undefined => {
  if (!activityUuid || !itemUuids.length) return undefined;

  const cacheKey = `${activityUuid}::${[...itemUuids].sort().join(",")}`;
  return presignedData[cacheKey];
};

/** Returns true when build-time pre-signed data is available. */
export const hasPresignedData = (): boolean =>
  Object.keys(presignedData).length > 0;
